import { MonoTypeOperatorFunction, defer, throwError, timer } from 'rxjs';
import { concatMap, retryWhen } from 'rxjs/operators';

/**
 * Política de reintentos para los errores transitorios de Databricks.
 *
 * Vive aparte del servicio a propósito: acá no se importa Angular ni `environment`, así que es
 * lógica pura y testeable sin levantar el grafo de módulos de la app.
 */

// === Warm-up del SQL warehouse ===
//
// Un SQL warehouse detenido tarda entre ~10s y varios minutos en levantar. Mientras arranca,
// Databricks responde 503 en vez de encolar la consulta: el statement NUNCA llega a ejecutarse, y
// por eso reintentar es seguro incluso para escrituras (el DELETE + MERGE del dashboard storage).
// Ese "nunca se ejecutó" es la razón por la que este es el único error que se reintenta solo; para
// cualquier otro no podemos saber si hubo efecto, así que se propaga sin tocar.
export const WARMUP_MAX_RETRIES = 4;
export const WARMUP_INITIAL_DELAY_MS = 3000; // Backoff exponencial: 3s, 6s, 12s, 15s = 36s
export const WARMUP_MAX_DELAY_MS = 15000;
export const CONCURRENCY_MAX_RETRIES = 3;
export const CONCURRENCY_INITIAL_DELAY_MS = 1000; // Backoff exponencial: 1s, 2s, 4s

// Gateway (502/504) del proxy. Pocos y cortos: si el proxy no responde, insistir no ayuda mucho y
// la operación puede estar corriendo del otro lado.
export const GATEWAY_MAX_RETRIES = 2;
export const GATEWAY_INITIAL_DELAY_MS = 1000; // Backoff exponencial: 1s, 2s

// === Timeout de una operación y presupuestos de espera ===
//
// El presupuesto de reintentos tiene que agotarse ANTES que el timeout que envuelve a la operación.
// Si no, el timeout externo mata el reintento a mitad de camino y el usuario recibe un TimeoutError
// genérico en vez del mensaje de warm-up, después de haber esperado igual.
//
// Para que no puedan desincronizarse, el timeout vive acá y los llamadores lo IMPORTAN en vez de
// declarar el suyo (ai-dashboard-storage.service.ts). Los presupuestos se derivan de él, así que
// cambiar el timeout reajusta los reintentos solo.
//
// Ojo también con la cola del ai-dashboard: es secuencial, así que una operación esperando el
// warm-up bloquea a las que están detrás. Otra razón para no estirar estos presupuestos.
export const DATABRICKS_OPERATION_TIMEOUT_MS = 60000;

// Margen para que el último intento alcance a completar dentro del timeout.
const RETRY_BUDGET_SAFETY_MARGIN_MS = 15000;

// Techo duro de espera acumulada: no se agenda un reintento si al terminar de esperar se superaría
// este total.
export const TRANSIENT_RETRY_MAX_TOTAL_WAIT_MS = DATABRICKS_OPERATION_TIMEOUT_MS - RETRY_BUDGET_SAFETY_MARGIN_MS;

// El chat anida dos operaciones con reintentos propios (startConversation → sendMessage), así que
// cada una se lleva una fracción del presupuesto para que la suma siga entrando en el timeout.
const GENIE_MAX_NESTED_OPERATIONS = 2;
export const GENIE_RETRY_MAX_TOTAL_WAIT_MS = Math.floor(TRANSIENT_RETRY_MAX_TOTAL_WAIT_MS / GENIE_MAX_NESTED_OPERATIONS);
// Backstop por si un mismo error matchea más de una categoría: tope de reintentos combinados.
export const TRANSIENT_RETRY_MAX_ATTEMPTS = WARMUP_MAX_RETRIES + CONCURRENCY_MAX_RETRIES + GATEWAY_MAX_RETRIES;

// Tope al texto que inspeccionamos: los errores de Databricks anidan stack traces enormes.
const MAX_ERROR_TEXT_LENGTH = 2000;

// El proxy puede envolver el 503 en un 200 con success:false, así que además del status miramos el
// texto. Los cuantificadores van acotados a propósito para que el regex no escale con la entrada.
const WARMUP_ERROR_PATTERN =
  /(warehouse|cluster|endpoint|compute)[^.]{0,60}(starting|start up|is not running|not available|warming)|TEMPORARILY_UNAVAILABLE|SERVICE_UNDER_MAINTENANCE|COULD_NOT_ACQUIRE_RESOURCES|CLUSTER_NOT_READY/i;

// Conflictos de escritura de Delta Lake: la transacción se abortó entera, así que se puede reintentar.
const DELTA_CONCURRENCY_PATTERN = /DELTA_CONCURRENT_APPEND|ConcurrentAppendException/i;

export enum GenieErrorType {
  AUTH_FAILED = 'AUTH_FAILED',
  RATE_LIMIT = 'RATE_LIMIT',
  TIMEOUT = 'TIMEOUT',
  NETWORK = 'NETWORK',
  UNKNOWN = 'UNKNOWN',
  DISABLED = 'DISABLED',
  /** El warehouse está levantando: transitorio, ya se reintentó y se agotó el presupuesto de espera. */
  WARMING_UP = 'WARMING_UP',
}

/**
 * Desenvuelve los errores que llegan como función.
 *
 * En rxjs 6 `throwError(x)` propaga `x` tal cual: no existe la sobrecarga de factory que agregó
 * rxjs 7. Como este repo escribe `throwError(() => error)` en todos lados, lo que viaja por la
 * cadena es la función, no el error (sendGenieMessage ya lo compensa a mano). Sin esto la detección
 * de warm-up no se activaría nunca en producción.
 */
export function unwrapError(error: any): any {
  if (typeof error !== 'function') return error;
  try {
    return error();
  } catch {
    return error;
  }
}

/**
 * Junta los textos de error de todas las capas (HttpErrorResponse, proxy, Databricks) en un solo
 * string acotado. Nunca tira: recibe tanto errores crudos como GenieError ya mapeados.
 */
export function collectErrorText(rawError: any): string {
  const error = unwrapError(rawError);
  const parts: string[] = [];
  const push = (value: any) => {
    if (typeof value === 'string' && value.length > 0) parts.push(value);
  };

  push(error?.message);
  push(error?.errorMessage);
  push(typeof error?.error === 'string' ? error.error : null);
  push(error?.error?.message);
  push(error?.error?.errorMessage);
  push(error?.serverResponse?.errorMessage);
  push(error?.originalError?.message);

  return parts.join(' | ').substring(0, MAX_ERROR_TEXT_LENGTH);
}

/** Extrae el status HTTP, venga del HttpErrorResponse crudo o del GenieError ya mapeado. */
export function extractErrorStatus(rawError: any): number | null {
  const error = unwrapError(rawError);
  const raw = error?.status ?? error?.originalError?.status ?? error?.serverResponse?.statusCode;
  const status = Number(raw);
  return Number.isFinite(status) && status > 0 ? status : null;
}

/**
 * Detecta si el error viene de un SQL warehouse que todavía está levantando (warm-up).
 *
 * Es el único error que reintentamos automáticamente, porque es el único donde sabemos que la
 * consulta no se ejecutó: Databricks rechaza con 503 antes de encolarla.
 */
export function isWarehouseWarmUpError(rawError: any): boolean {
  const error = unwrapError(rawError);
  if (!error) return false;

  // Deshabilitado por configuración: no es transitorio, reintentar nunca lo va a resolver.
  if (error.type === GenieErrorType.DISABLED) return false;

  if (error.type === GenieErrorType.WARMING_UP) return true;
  if (extractErrorStatus(error) === 503) return true;

  return WARMUP_ERROR_PATTERN.test(collectErrorText(error));
}

/** Conflicto de escritura concurrente de Delta Lake: la transacción se abortó entera. */
export function isDeltaConcurrencyError(error: any): boolean {
  if (!error) return false;
  return DELTA_CONCURRENCY_PATTERN.test(collectErrorText(error));
}

/**
 * Error de gateway (502/504): el proxy no pudo hablar con Databricks, o tardó de más.
 *
 * OJO: a diferencia del warm-up, esto NO garantiza que la operación no se haya ejecutado — un 502
 * puede llegar después de que el backend hizo el trabajo. Por eso solo se reintenta donde el
 * llamador declara que la operación es idempotente (`retryGatewayErrors`), típicamente un GET.
 */
export function isGatewayError(rawError: any): boolean {
  const error = unwrapError(rawError);
  if (!error) return false;
  if (error.type === GenieErrorType.DISABLED) return false;

  const status = extractErrorStatus(error);
  return status === 502 || status === 504;
}

export type TransientRetryReason = 'warm-up' | 'delta-concurrency' | 'gateway';

export interface TransientRetryOptions {
  /** Nombre de la operación, solo para los logs. */
  label: string;
  maxTotalWaitMs?: number;
  /**
   * Habilita el reintento de 502/504. Activar SOLO en operaciones idempotentes (GET): un gateway
   * error no garantiza que la operación no se haya ejecutado del otro lado.
   */
  retryGatewayErrors?: boolean;
  /** Si devuelve true, no se reintenta nada. */
  isCircuitBreakerOpen?: () => boolean;
  onRetry?: (info: { label: string; reason: TransientRetryReason; delayMs: number; elapsedMs: number }) => void;
  onGiveUp?: (info: { label: string; reason: string }) => void;
}

/**
 * Reintenta errores transitorios de Databricks: warehouse levantando y concurrencia de Delta Lake.
 * Cualquier otro error se propaga tal cual, sin reintentar.
 *
 * Las cotas son simultáneas, y todas hacen falta: el peor caso tiene que quedar acotado aunque el
 * llamador tenga su propio retry encima (dashboard-storage-api.provider.ts reintenta 3 veces más).
 * - por categoría: WARMUP_MAX_RETRIES / CONCURRENCY_MAX_RETRIES
 * - global: TRANSIENT_RETRY_MAX_ATTEMPTS reintentos combinados
 * - tiempo: no se agenda una espera que exceda maxTotalWaitMs acumulados desde la suscripción
 * - circuit breaker: si está abierto, no se reintenta nada
 */
export function retryTransientDatabricksErrors<T>(options: TransientRetryOptions): MonoTypeOperatorFunction<T> {
  const {
    label,
    maxTotalWaitMs = TRANSIENT_RETRY_MAX_TOTAL_WAIT_MS,
    retryGatewayErrors = false,
    isCircuitBreakerOpen,
    onRetry,
    onGiveUp,
  } = options;

  // defer: cada suscripción (incluidas las que genera un retry externo) arranca con contadores y
  // presupuesto propios, en vez de heredar el estado de la primera.
  return source =>
    defer(() => {
      const startedAt = Date.now();
      let warmUpRetries = 0;
      let concurrencyRetries = 0;
      let gatewayRetries = 0;

      const giveUp = (error: any, reason: string) => {
        if (onGiveUp) onGiveUp({ label, reason });
        return throwError(error);
      };

      return source.pipe(
        retryWhen(errors =>
          errors.pipe(
            // concatMap: espera a que termine el timer antes de procesar el error siguiente.
            concatMap((error, index) => {
              if (index >= TRANSIENT_RETRY_MAX_ATTEMPTS) {
                return giveUp(error, `tope global de reintentos alcanzado (${TRANSIENT_RETRY_MAX_ATTEMPTS})`);
              }

              if (isCircuitBreakerOpen && isCircuitBreakerOpen()) {
                return giveUp(error, 'circuit breaker abierto');
              }

              const isWarmUp = isWarehouseWarmUpError(error);
              const isConcurrency = !isWarmUp && isDeltaConcurrencyError(error);
              // El gateway solo cuenta si el llamador declaró la operación idempotente.
              const isGateway = !isWarmUp && !isConcurrency && retryGatewayErrors && isGatewayError(error);
              // No es transitorio: se propaga sin reintentar y sin log de "me rindo".
              if (!isWarmUp && !isConcurrency && !isGateway) return throwError(error);

              let delayMs: number;
              let reason: TransientRetryReason;
              if (isWarmUp) {
                if (warmUpRetries >= WARMUP_MAX_RETRIES) {
                  return giveUp(error, `el warehouse sigue sin levantar tras ${warmUpRetries} reintentos`);
                }
                delayMs = Math.min(WARMUP_INITIAL_DELAY_MS * Math.pow(2, warmUpRetries), WARMUP_MAX_DELAY_MS);
                warmUpRetries++;
                reason = 'warm-up';
              } else if (isConcurrency) {
                if (concurrencyRetries >= CONCURRENCY_MAX_RETRIES) {
                  return giveUp(error, `conflicto de concurrencia persistente tras ${concurrencyRetries} reintentos`);
                }
                delayMs = CONCURRENCY_INITIAL_DELAY_MS * Math.pow(2, concurrencyRetries);
                concurrencyRetries++;
                reason = 'delta-concurrency';
              } else {
                if (gatewayRetries >= GATEWAY_MAX_RETRIES) {
                  return giveUp(error, `gateway sigue fallando tras ${gatewayRetries} reintentos`);
                }
                delayMs = GATEWAY_INITIAL_DELAY_MS * Math.pow(2, gatewayRetries);
                gatewayRetries++;
                reason = 'gateway';
              }

              const elapsedMs = Date.now() - startedAt;
              if (elapsedMs + delayMs > maxTotalWaitMs) {
                return giveUp(error, `presupuesto de espera agotado (${elapsedMs}ms de ${maxTotalWaitMs}ms)`);
              }

              if (onRetry) onRetry({ label, reason, delayMs, elapsedMs });

              return timer(delayMs);
            })
          )
        )
      );
    });
}
